/* eslint-disable react/no-this-in-sfc */
import * as React from 'react';
import { connect } from 'react-redux';
import { isEmpty, isNil } from 'lodash';

import BackToTopControl from 'components/back-to-top-control/BackToTopControl';
import BaseSubcampaignPage from './BaseSubcampaignPage';
import SubcampaignOfferView from '../components/subcampaign-offer/SubcampaignOfferView';
import SubcampaignOfferViewFooter from '../components/subcampaign-offer-footer/SubcampaignOfferFooter';
import PageHeading from 'components/page/page-heading/PageHeading';
import CampaignCard from 'modules/campaigns/components/campaign-card/CampaignCard';

import { downloadIndividualOffer } from 'modules/campaigns/reducers/campaignIndividualOffer';

import { t } from 'utils/language';

import type {
  BrandProfile,
  Campaign,
  CampaignOverview,
  Deliverable,
  SubcampaignWithInfluencerSettings
} from 'types';

type OwnProps = {
  params: {
    id: string,
    subcampaignId: string
  }
};

type StateProps = {
  isCampaignLoaded: boolean,
  subcampaign?: SubcampaignWithInfluencerSettings,
  brandProfile: BrandProfile,
  campaign: Campaign,
  campaignOverview: CampaignOverview,
  offerDeliverables: Array<Deliverable>
};

type DispatchProps = {
  dispatchDownloadIndividualOffer: (b: number, a: boolean) => void
};

type Props = OwnProps & StateProps & DispatchProps;
type RendererReturn = (a: boolean) => React.ReactNode;

class SubcampaignOfferViewPage extends React.PureComponent<Props> {
  handleDownloadOffer = () => {
    const { subcampaign, dispatchDownloadIndividualOffer } = this.props;

    if (subcampaign) {
      dispatchDownloadIndividualOffer(subcampaign.id, false);
    }
  };

  getHeaderRenderer(): RendererReturn {
    const {
      isCampaignLoaded,
      brandProfile,
      campaign,
      campaignOverview
    } = this.props;

    return () => (
      isCampaignLoaded && !isEmpty(brandProfile) && (
        <React.Fragment>
          <PageHeading
            heading={t('subcampaign_offer.campaign_background')}
            color="light"
            size="h1"
            margin="none"
            customClass="subcampaign-offer__section-header"
          />
          <CampaignCard
            layout="horizontal"
            campaign={campaign}
            campaignOverview={campaignOverview}
            brandProfile={brandProfile}
            showExpanded
            onDownloadOffer={this.handleDownloadOffer}
          />
        </React.Fragment>
      )
    );
  }

  getFooterRenderer(): RendererReturn {
    const {
      subcampaign,
      isCampaignLoaded
    } = this.props;
    const hasNoSubcampaigns = !(subcampaign && subcampaign.creatorProfile);

    return (isLoading: boolean) => {
      if (!isLoading && isCampaignLoaded && hasNoSubcampaigns) {
        return <SubcampaignOfferViewFooter />;
      }

      return null;
    };
  }

  render() {
    const {
      subcampaign,
      brandProfile,
      campaign,
      offerDeliverables,
      params
    } = this.props;

    if (isNil(campaign)) {
      return null;
    }

    return (
      <BaseSubcampaignPage
        params={params}
        documentTitle={t('offer_page')}
        renderHeader={this.getHeaderRenderer()}
        renderFooter={this.getFooterRenderer()}
      >
        {
          subcampaign && subcampaign.creatorProfile && (
            <SubcampaignOfferView
              isForBrand={false}
              influencerProfile={subcampaign.creatorProfile}
              subcampaign={subcampaign}
              brandProfile={brandProfile}
              campaign={campaign}
              offerDeliverables={offerDeliverables}
            />
          )
        }
        <BackToTopControl
          isLoading={!subcampaign}
          show
        />
      </BaseSubcampaignPage>
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchDownloadIndividualOffer: (subcampaignId: number, isForBrand: boolean) =>
    dispatch(downloadIndividualOffer(subcampaignId, isForBrand))
});


const mapStateToProps = (
  {
    subcampaign,
    brandProfile,
    campaignBrief,
    campaignOverview,
    offerDeliverables
  }
): StateProps => ({
  isCampaignLoaded: campaignBrief.brief && !isNil(campaignBrief.brief.id),
  subcampaign: subcampaign.data,
  brandProfile: brandProfile.profile,
  campaign: campaignBrief.brief,
  campaignOverview: campaignOverview.overview,
  offerDeliverables: (subcampaign.data && subcampaign.data.offer && offerDeliverables.deliverablesByOfferId[subcampaign.data.offer.id]) || []
});

export default connect(mapStateToProps, mapDispatchToProps)(SubcampaignOfferViewPage);
